import { Component, Input, OnInit } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormControl } from '@angular/forms';
import { TableComponent } from '../../../core/table/table.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { SelectComponent } from '../../../core/controls/select.component';
import {
  LoadingPanelComponent,
  LoadingState,
} from '../../../core/panels/loading-panel.component';
import { BaseComponent } from '../../../base/base.component';
import { getClaimKey } from '../../../../functions/get-claim-key';
import { DateEx } from '../../../../functions/date-ex';
import { CasePipe } from '../../../../pipes/case.pipe';
import { AcronymPipe } from '../../../../pipes/acronym.pipe';

@Component({
  selector: 'app-reserves-unallocated-expense-tab',
  imports: [
    CommonModule,
    TableComponent,
    ColumnComponent,
    SelectComponent,
    LoadingPanelComponent,
    CasePipe,
    AcronymPipe,
  ],
  template: `
    <app-loading-panel
      [state]="loadingState"
      loadingStyle="Skeleton"
      [loadingHeight]="215"
    >
      <ng-template #loaded>
        <div class="tw-flex tw-flex-row tw-w-full tw-justify-end">
          <div class="tw-flex tw-flex-col tw-w-1/4 tw-gap-0 tw-p-0 tw-m-0">
            <app-select
              [control]="dateControl"
              label="Date"
              optionLabel="dateEffective"
              optionValue="dateEffective"
              [options]="unallocatedDates"
              [filter]="false"
              [virtualScroll]="false"
            />
          </div>
        </div>
        <div class="tw-flex tw-flex-row tw-w-full tw-mt-[-60px]">
          <app-table
            class="highlightable-table tw-w-full"
            [dataSource]="unallocatedExpenses"
            [scrollable]="false"
            [expandable]="false"
            dataKey="sequenceNum"
            recordLabel="Item"
            [showPaginator]="false"
            [stripedRows]="true"
            [showFilter]="false"
            [showFilterButton]="false"
            [theme]="'Dark'"
          >
            <app-column name="categoryDesc" label="Category" [sort]="false">
              <ng-template let-row>
                @if (row.categoryDesc === 'Total Unallocated Expense') {
                  <span class="highlight-row">
                    {{ row.categoryDesc | case: 'sentence' }}
                  </span>
                } @else {
                  {{ row.categoryDesc | case: 'sentence' | acronym }}
                }
              </ng-template>
            </app-column>
            <app-column name="paid" label="Paid" format="c" [sort]="false" />
            <app-column
              name="scheduled"
              label="Scheduled"
              format="c"
              [sort]="false"
            />
            <app-column
              name="reserve"
              label="Reserve"
              format="c"
              [sort]="false"
            />
            <app-column
              name="incurred"
              label="Incurred"
              format="c"
              [sort]="false"
            />
          </app-table>
        </div>
      </ng-template>
    </app-loading-panel>
  `,
  styles: ``,
})
export class ReservesUnallocatedExpenseTabComponent
  extends BaseComponent
  implements OnInit
{
  @Input()
  reserveNumber = 0;

  unallocatedExpenses: any[] = [];
  unallocatedDates: any[] = [];
  dateControl = new FormControl<Date>(null);
  loadingState: LoadingState = 'Init';
  private loaded = false;
  private datePipe = new DatePipe('en-US');
  dateEffective: Date;

  async ngOnInit() {
    if (this.loaded) return;

    this.loadingState = 'Loading';
    try {
      const dates = await this.api.reserves.getUnallocatedDates(getClaimKey());
      const sortedDates = [...dates].sort((a, b) => {
        const dateA = new Date(a.dateEffective || 0).getTime();
        const dateB = new Date(b.dateEffective || 0).getTime();
        return dateB - dateA;
      });

      this.unallocatedDates = sortedDates.map((date) => ({
        ...date,
        dateEffective:
          this.datePipe.transform(date.dateEffective, 'MM/dd/yyyy') ||
          date.dateEffective,
      }));

      let mostRecentDate: string | null = null;
      if (this.unallocatedDates.length) {
        mostRecentDate = this.unallocatedDates[0].dateEffective;
      }

      if (mostRecentDate) {
        this.dateControl.setValue(mostRecentDate as any);
      }

      this.dispose.subs = this.dateControl.valueChanges.subscribe(
        async (dateValue) => {
          if (dateValue) {
            await this.loadExpenses(DateEx.clearTime(dateValue));
          }
        },
      );

      this.dateEffective = mostRecentDate ? new Date(mostRecentDate) : null;
      await this.loadExpenses(DateEx.clearTime(this.dateEffective));
      this.loaded = true;
    } finally {
      this.loadingState = 'Loaded';
    }
  }

  private async loadExpenses(dateValue?: any) {
    const selectedDate = this.parseDateString(dateValue);
    this.loadingState = 'Loading';
    try {
      this.unallocatedExpenses = await this.api.reserves.getUnallocatedExpense(
        getClaimKey(),
        this.reserveNumber,
        selectedDate,
      );
    } finally {
      this.loadingState = 'Loaded';
    }
  }

  private parseDateString(dateValue: any): Date | undefined {
    if (!dateValue) {
      return undefined;
    }

    if (dateValue instanceof Date) {
      return dateValue;
    }

    if (typeof dateValue === 'string') {
      const parsed = new Date(dateValue);
      if (!isNaN(parsed.getTime())) {
        return parsed;
      }
    }

    return undefined;
  }
}
